import { useActiveBookingContext, useBookingEngineStateContext, usePropertyContext } from "@app/app/hooks";
import type { IBooking } from "@app/app/types";
import type { IExchangeRates, TBookableItem } from "@app/common/types";
import { cn } from "@app/componentLibrary/lib/utils";
import { LoyaltyContext } from "@app/loyalty/context/LoyaltyContext";
import { ITaxesAndFees } from "@app/room/types";
import { convertCurrency } from "@app/room/utils";
import { MoneyComponent } from "@hotelchamp/common";
import classNames from "classnames";
import { TFunction } from "i18next";
import React, { Fragment, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useGetPromoDiscount } from "../hooks";
import { getRateBreakdown, IRateBreakdownResult } from "../utils/rateUtils";

export interface IPriceableItemPriceProps extends React.HTMLAttributes<HTMLDivElement> {
    item: TBookableItem | number | undefined;
    discountedPrice?: number;
    booking?: IBooking;
    moneyClassName?: string;
    outdatedMoneyClassName?: string;
    outdatedMoneyStrikeThroughClassName?: string;
    plain?: boolean;
    compact?: boolean;
    priceUnit?: string;
    displayTaxesFirst?: boolean;
    taxesAndFees?: ITaxesAndFees;
}

const largeFormatCurrencies = ["EGP", "HKD", "IDR", "INR", "ISK", "JPY", "KRW", "MXN", "RUB", "THB", "TWD", "TRY", "UAH", "ZAR"];

// Helper function to calculate the amount, applying rounding if needed
const calculateAmount = (baseAmount: number, currencyCode: string) => {
    // Round if explicitly requested or if the currency is in the largeFormatCurrencies list
    const shouldRound = largeFormatCurrencies.includes(currencyCode);

    return (shouldRound ? Math.round(baseAmount) : baseAmount) / 100;
};

const getTaxAndFeesElement = (
    t: TFunction,
    taxesAndFees: ITaxesAndFees,
    priceUnit: string | undefined,
    currency: string,
    currencyFormat: Intl.NumberFormatOptions | undefined,
    exchangeRates: IExchangeRates,
    rateBreakdown: IRateBreakdownResult,
    displayTaxesFirst?: boolean
): React.ReactNode => {
    const totalIncludedTaxes = taxesAndFees.included.reduce((total, tax) => total + (tax.cost || 0), 0);
    const totalExclusiveTaxes = convertCurrency(
        exchangeRates,
        taxesAndFees.excluded.concat(taxesAndFees.to_be_paid_later).reduce((total, tax) => total + (tax.cost || 0), 0),
        currency
    );
    const includedTaxesText = taxesAndFees.included.map((tax) => t(`taxNames.${tax.name}`)).join(" & ");

    if (totalIncludedTaxes <= 0 && totalExclusiveTaxes <= 0) {
        return undefined;
    }

    return (
        <div className={classNames("text-sm text-gray-400 flex flex-wrap gap-x-1", { "justify-end": displayTaxesFirst })}>
            {totalIncludedTaxes > 0 && (
                <span>
                    {t("taxesIncluded", { taxes: includedTaxesText, interpolation: { escapeValue: false } })}
                    {totalExclusiveTaxes > 0 && ","}
                </span>
            )}

            {totalExclusiveTaxes > 0 && (
                <span className="flex gap-x-1">
                    <Trans
                        i18nKey="taxesExcluded"
                        ns="common"
                        components={[
                            <div className="flex gap-x-[2px]">
                                <MoneyComponent
                                    amount={calculateAmount(totalExclusiveTaxes, rateBreakdown.targetCurrency)}
                                    locale={rateBreakdown.locale}
                                    currency={currency}
                                    formatOptions={currencyFormat}
                                />
                                {priceUnit && (
                                    <Fragment>
                                        <span className="text-sm whitespace-nowrap">/</span>
                                        <span className="text-sm whitespace-nowrap">{priceUnit}</span>
                                    </Fragment>
                                )}
                            </div>,
                        ]}
                    />
                </span>
            )}
        </div>
    );
};

export const PriceableItemPrice = ({
    item,
    discountedPrice,
    booking,
    className,
    moneyClassName,
    outdatedMoneyClassName,
    outdatedMoneyStrikeThroughClassName,
    plain = false,
    compact = false,
    priceUnit = undefined,
    displayTaxesFirst = false,
    taxesAndFees = undefined,
}: IPriceableItemPriceProps) => {
    const { activePropertyId } = usePropertyContext();
    const { getState, getLengthOfStay, exchangeRates } = useBookingEngineStateContext();
    const { activeBookingIndex } = useActiveBookingContext();
    const { promoCode, bookingState, currency, language } = getState();
    const { accessToken } = useContext(LoyaltyContext);
    const { data: promoDiscount } = useGetPromoDiscount(
        promoCode || "",
        activePropertyId,
        { enabled: !!promoCode && !!activePropertyId },
        accessToken
    );
    const bookings = bookingState?.bookings || [];
    const activeBooking = booking || (activeBookingIndex !== -1 ? bookings[activeBookingIndex] : null);
    const { t } = useTranslation(["common"]);

    const rateBreakdown = getRateBreakdown({
        los: getLengthOfStay(),
        bookingEngineState: getState(),
        booking: booking || activeBooking || undefined,
        promoDiscount,
        adultCount: activeBooking?.adultsCount,
        priceableItem: item,
        exchangeRates,
        forcedDiscountedPrice: discountedPrice,
    });

    if (!item) {
        return null;
    }

    let currencyFormat: Intl.NumberFormatOptions | undefined;

    if (largeFormatCurrencies.includes(currency) || compact) {
        currencyFormat = {
            style: "currency",
            currency,
            currencyDisplay: "narrowSymbol",
            notation: compact ? "compact" : "standard",
            compactDisplay: compact ? "long" : "short",
            maximumSignificantDigits: compact ? 3 : undefined,
            maximumFractionDigits: 0,
        };
    }

    let taxAndFeesDisplayElement: React.ReactNode | undefined;

    if (typeof taxesAndFees !== "undefined") {
        taxAndFeesDisplayElement = getTaxAndFeesElement(
            t,
            taxesAndFees,
            priceUnit,
            currency,
            currencyFormat,
            exchangeRates,
            rateBreakdown,
            displayTaxesFirst
        );
    }

    if (plain) {
        return item && rateBreakdown.exchangedBaseRate ? (
            <MoneyComponent
                amount={calculateAmount(
                    rateBreakdown.exchangedDiscountedRate ? rateBreakdown.exchangedDiscountedRate : rateBreakdown.exchangedBaseRate,
                    rateBreakdown.targetCurrency
                )}
                locale={rateBreakdown.locale}
                currency={rateBreakdown.targetCurrency}
                formatOptions={currencyFormat}
            />
        ) : null;
    }

    return item && rateBreakdown.exchangedBaseRate ? (
        <div
            data-testid="priceable-item"
            className={cn("flex flex-wrap items-center gap-x-2", className)}
            itemScope={true}
            itemType="https://schema.org/CompoundPriceSpecification"
            itemProp="priceSpecification"
        >
            {displayTaxesFirst && taxAndFeesDisplayElement}

            <div className="flex flex-wrap gap-x-2" style={{ justifyContent: "inherit" }}>
                {!!rateBreakdown.exchangedDiscountedRate && (
                    <div data-testid="priceable-item-discounted" style={{ lineHeight: "0px" }}>
                        <del className={cn("relative text-gray-400 text-sm no-underline", outdatedMoneyClassName)}>
                            <meta itemProp="priceCurrency" content={currency} />
                            <meta itemProp="price" content={calculateAmount(rateBreakdown.exchangedBaseRate, currency).toString()} />
                            <meta itemProp="valueAddedTaxIncluded" content="False" />
                            <MoneyComponent
                                amount={calculateAmount(rateBreakdown.exchangedBaseRate, currency)}
                                locale={rateBreakdown.locale}
                                currency={currency}
                                formatOptions={currencyFormat}
                            />
                            <div
                                className={cn(
                                    "absolute h-0 w-full border-t mx-0.5 border-t-gray-400 border-b border-b-gray-200 top-1/2",
                                    outdatedMoneyStrikeThroughClassName
                                )}
                            />
                        </del>
                    </div>
                )}

                <div className="flex items-center gap-x-0.5">
                    <span data-testid="priceable-item-original" className={cn("text-base text-gray-900 leading-none", moneyClassName)}>
                        <meta itemProp="priceCurrency" content={currency} />
                        <meta itemProp="price" content={calculateAmount(rateBreakdown.exchangedBaseRate, currency).toString()} />
                        <meta itemProp="valueAddedTaxIncluded" content="False" />
                        <MoneyComponent
                            amount={calculateAmount(
                                rateBreakdown.exchangedDiscountedRate
                                    ? rateBreakdown.exchangedDiscountedRate
                                    : rateBreakdown.exchangedBaseRate,
                                currency
                            )}
                            locale={rateBreakdown.locale}
                            currency={currency}
                            formatOptions={currencyFormat}
                        />
                    </span>

                    {typeof priceUnit !== "undefined" && (
                        <Fragment>
                            <span className="text-sm text-gray-900 whitespace-nowrap">/</span>
                            <span className="text-sm text-gray-900 whitespace-nowrap">{priceUnit}</span>
                        </Fragment>
                    )}
                </div>
            </div>

            {!displayTaxesFirst && taxAndFeesDisplayElement}

            <div className="hidden" itemScope itemType="https://schema.org/UnitPriceSpecification" itemProp="priceComponent">
                <meta itemProp="price" content={calculateAmount(rateBreakdown.exchangedBaseRate, currency).toString()} />
                <meta itemProp="priceCurrency" content={currency} />
            </div>
        </div>
    ) : null;
};
